/**
 * Field markers (MarkerSnap): the placed Magicks and the jotunn's tells.
 *
 * Three passes, like everything else on the field: `drawMarkerFloor` in the
 * ground layer (meteor shadows, the vortex, storm and tornado shadows, the
 * tells), `drawMarkerBody` in the y-sorted body pass for what stands up
 * (the tornado's funnel, the elemental, a falling rock), and
 * `drawMarkerSky` after the bodies for the storm cloud overhead.
 *
 * Gameplay areas are drawn at their true size (the ground is 1:1), so a
 * tell's red band or frost ring is exactly where the hit lands.
 */

import { BOSS, MAGICK, type Element } from '@shared/index.js';
import type { ViewEnemy, ViewMarker } from '../state/viewModel.js';
import { ELEMENT_SWATCH, elementColor, mixHex, rgba } from '../palette.js';
import { clamp01, drawGlow, drawShadow, hashRand, TAU, UNIT, type Ctx } from './gfx.js';
import type { Camera } from './camera.js';
import { PC, PK, type Particles } from './particles.js';

/** The rock falls from this high (world units) over MAGICK.meteorWarn, coming in from the upper left. */
const METEOR_FALL_H = 13;
const METEOR_SLANT = 0.45;
const TORNADO_H = 3.2;
const STORM_CLOUD_H = 5.2;
const ELEMENTAL_H = 1.7;
/** The elemental's slam pose lasts this long after it lands (s). */
const SLAM_POSE_S = 0.35;

const ICE = ELEMENT_SWATCH.ice;
const STEAM = ELEMENT_SWATCH.steam;
const EARTH = ELEMENT_SWATCH.earth;
const ARCANE = ELEMENT_SWATCH.arcane;

/** The colour a fed tornado or elemental glows with: the newest element it carries, or its own. */
function fedColor(el: readonly Element[], own: string): string {
  const last = el[el.length - 1];
  return last ? elementColor(last) : own;
}

/** True for the markers that stand up and take part in the y-sort. */
export function markerStandsUp(m: ViewMarker): boolean {
  return m.k === 'tornado' || m.k === 'elemental' || (m.k === 'meteor' && m.t <= MAGICK.meteorWarn);
}

/**
 * How far a tell has grown, 0..1 from its start to the strike: against its full length from the server, which Hard,
 * Brutal and the rage shorten (`normal`, Normal's length, for a marker that doesn't say).
 */
export function tellProgress(m: Pick<ViewMarker, 't' | 'tt'>, normal: number): number {
  return clamp01(1 - m.t / (m.tt ?? normal));
}

// ------------------------------------------------------------------ floor

export function drawMarkerFloor(ctx: Ctx, cam: Camera, m: ViewMarker, time: number, particles: Particles, dt: number): void {
  const u = UNIT;
  const px = cam.sx(m.rx), py = cam.sy(m.ry);
  switch (m.k) {
    case 'meteor': {
      if (m.t > MAGICK.meteorWarn) return;
      const k = clamp01(1 - m.t / MAGICK.meteorWarn);
      const r = m.r * u;
      // The landing zone: a dashed ring at the true blast size, filling in as the rock comes down.
      ctx.fillStyle = rgba('#000000', 0.12 + 0.3 * k);
      ctx.beginPath(); ctx.ellipse(px, py, r * (0.35 + 0.65 * k), r * (0.35 + 0.65 * k), 0, 0, TAU); ctx.fill();
      ctx.strokeStyle = rgba(ELEMENT_SWATCH.fire.core, 0.35 + 0.55 * k);
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 5]);
      ctx.lineDashOffset = -time * 30;
      ctx.beginPath(); ctx.arc(px, py, r, 0, TAU); ctx.stroke();
      ctx.setLineDash([]);
      return;
    }
    case 'storm': {
      const fade = Math.min(1, m.t, (MAGICK.stormSeconds - m.t) * 2);
      const r = m.r * u;
      ctx.fillStyle = rgba('#10142A', 0.2 * fade);
      ctx.beginPath(); ctx.arc(px, py, r, 0, TAU); ctx.fill();
      // Drifting cloud shadows.
      for (let i = 0; i < 6; i++) {
        const a = time * 0.25 + i * 1.7 + m.id;
        const d = r * (0.25 + 0.5 * hashRand(m.id, 70 + i));
        ctx.fillStyle = rgba('#05060F', 0.12 * fade);
        ctx.beginPath(); ctx.arc(px + Math.cos(a) * d, py + Math.sin(a) * d, r * 0.3, 0, TAU); ctx.fill();
      }
      ctx.strokeStyle = rgba(ELEMENT_SWATCH.lightning.core, 0.25 * fade);
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 6]);
      ctx.beginPath(); ctx.arc(px, py, r, 0, TAU); ctx.stroke();
      ctx.setLineDash([]);
      return;
    }
    case 'vortex': {
      const k = clamp01(1 - m.t / MAGICK.vortexSeconds);
      const r = m.r * u;
      ctx.strokeStyle = rgba(ARCANE.core, 0.18);
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.arc(px, py, r, 0, TAU); ctx.stroke();
      // Four arms spiralling in, faster as it nears the burst.
      ctx.globalCompositeOperation = 'lighter';
      const spin = time * (4 + 6 * k);
      for (let arm = 0; arm < 4; arm++) {
        ctx.strokeStyle = rgba(arm % 2 === 0 ? ARCANE.core : ARCANE.soft, 0.55);
        ctx.lineWidth = 3;
        ctx.beginPath();
        for (let s = 0; s <= 24; s++) {
          const f = s / 24;
          const a = spin + arm * (TAU / 4) + f * 4.2;
          const d = r * (1 - f) * (1 - 0.3 * k);
          const x = px + Math.cos(a) * d, y = py + Math.sin(a) * d;
          if (s === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      drawGlow(ctx, ARCANE.core, px, py, (1.2 + 1.6 * k) * u);
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = rgba('#120008', 0.85);
      ctx.beginPath(); ctx.arc(px, py, (0.25 + 0.55 * k) * u, 0, TAU); ctx.fill();
      // Motes drawn inward.
      if (Math.random() < dt * 40) {
        const a = Math.random() * TAU;
        const d = m.r * (0.6 + 0.4 * Math.random());
        const col = particles.colorIndex(ARCANE.core);
        particles.spawn(PK.sparkle, col, m.rx + Math.cos(a) * d, m.ry + Math.sin(a) * d, 0.2, -Math.cos(a) * 6, -Math.sin(a) * 6, 0, 0.6, 4, 0, 0.98);
      }
      return;
    }
    case 'tornado': {
      const r = m.r * u;
      drawShadow(ctx, px, py, r * 1.1, r * 0.55, 0.35);
      ctx.strokeStyle = rgba(STEAM.core, 0.4);
      ctx.lineWidth = 2;
      for (let i = 0; i < 3; i++) {
        const a = -time * 7 + i * 2.1;
        ctx.beginPath(); ctx.ellipse(px, py, r * (0.6 + i * 0.25), r * (0.3 + i * 0.12), 0, a, a + 1.6); ctx.stroke();
      }
      if (Math.random() < dt * 20) particles.dust(m.rx, m.ry, 1, 3);
      return;
    }
    case 'elemental':
      drawShadow(ctx, px, py, m.r * u * 1.1, m.r * u * 0.45, 0.45);
      return;
    case 'fissure': {
      // The crack's danger band at its true length and width, brightening to the strike.
      const k = tellProgress(m, BOSS.fissureTell);
      const len = m.r * u;
      const half = 0.95 * u;
      ctx.save();
      ctx.translate(px, py);
      ctx.rotate(m.ang);
      ctx.fillStyle = rgba('#FF3A2A', 0.12 + 0.22 * k + 0.08 * Math.sin(time * 30));
      ctx.fillRect(0, -half, len, half * 2);
      ctx.strokeStyle = rgba('#FFE2C8', 0.5 + 0.5 * k);
      ctx.lineWidth = 2 + 2 * k;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      const steps = 10;
      const reach = Math.min(1, 0.25 + k);
      for (let s = 1; s <= steps; s++) {
        const f = s / steps;
        if (f > reach) break;
        ctx.lineTo(len * f, (hashRand(m.id, 130 + s) - 0.5) * half * 0.9);
      }
      ctx.stroke();
      ctx.restore();
      return;
    }
    case 'frost': {
      const k = tellProgress(m, BOSS.slamTell);
      const r = m.r * u;
      ctx.fillStyle = rgba(ICE.core, 0.1 + 0.18 * k);
      ctx.beginPath(); ctx.arc(px, py, r, 0, TAU); ctx.fill();
      ctx.strokeStyle = rgba('#FFFFFF', 0.55 + 0.35 * Math.sin(time * 18));
      ctx.lineWidth = 2;
      ctx.setLineDash([8, 6]);
      ctx.beginPath(); ctx.arc(px, py, r, 0, TAU); ctx.stroke();
      ctx.setLineDash([]);
      // The frost creeping out from its feet: when it reaches the dashed ring, the slam lands.
      ctx.strokeStyle = rgba(ICE.soft, 0.9);
      ctx.lineWidth = 3;
      ctx.beginPath(); ctx.arc(px, py, r * k, 0, TAU); ctx.stroke();
      if (Math.random() < dt * 30) particles.frost(m.rx + (Math.random() - 0.5) * m.r * 2 * k, m.ry + (Math.random() - 0.5) * m.r * 2 * k, 0, 1);
      return;
    }
  }
}

/** Under an enraged boss: a cold glow and frost lifting off it. */
export function drawBossRage(ctx: Ctx, cam: Camera, e: ViewEnemy, time: number, particles: Particles, dt: number): void {
  if (e.ph !== 2) return;
  const px = cam.sx(e.rx), py = cam.sy(e.ry);
  const pulse = 1 + 0.12 * Math.sin(time * 5);
  ctx.globalCompositeOperation = 'lighter';
  drawGlow(ctx, ICE.core, px, py, 3.2 * UNIT * pulse);
  ctx.globalCompositeOperation = 'source-over';
  if (Math.random() < dt * 25) particles.frost(e.rx, e.ry, 0.4 + Math.random() * 1.6, 1);
}

// ------------------------------------------------------------------ bodies (y-sorted)

export function drawMarkerBody(ctx: Ctx, cam: Camera, m: ViewMarker, time: number, particles: Particles, dt: number): void {
  switch (m.k) {
    case 'meteor': drawMeteorRock(ctx, cam, m, time, particles, dt); return;
    case 'tornado': drawTornado(ctx, cam, m, time, particles, dt); return;
    case 'elemental': drawElemental(ctx, cam, m, time, particles, dt); return;
    default: return;
  }
}

function drawMeteorRock(ctx: Ctx, cam: Camera, m: ViewMarker, time: number, particles: Particles, dt: number): void {
  if (m.t > MAGICK.meteorWarn) return;
  const u = UNIT;
  const f = clamp01(m.t / MAGICK.meteorWarn);
  const h = METEOR_FALL_H * f * f;
  const wx = m.rx - h * METEOR_SLANT;
  const px = cam.sx(wx), py = cam.sy(m.ry) - h * u;
  // The trail back up the way it came.
  ctx.globalCompositeOperation = 'lighter';
  const tail = 2.2 * u;
  const tx = px - tail * METEOR_SLANT, ty = py - tail;
  const grad = ctx.createLinearGradient(px, py, tx, ty);
  grad.addColorStop(0, rgba(ELEMENT_SWATCH.fire.core, 0.9));
  grad.addColorStop(1, rgba(ELEMENT_SWATCH.fire.core, 0));
  ctx.strokeStyle = grad;
  ctx.lineWidth = 0.5 * u;
  ctx.lineCap = 'round';
  ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(tx, ty); ctx.stroke();
  ctx.lineCap = 'butt';
  drawGlow(ctx, ELEMENT_SWATCH.lava.core, px, py, 1.6 * u);
  ctx.globalCompositeOperation = 'source-over';
  const r = 0.42 * u;
  ctx.fillStyle = EARTH.dark;
  ctx.beginPath();
  for (let i = 0; i < 7; i++) {
    const a = i * (TAU / 7) + time * 3 + m.id;
    const rr = r * (0.8 + 0.3 * hashRand(m.id, 50 + i));
    if (i === 0) ctx.moveTo(px + Math.cos(a) * rr, py + Math.sin(a) * rr); else ctx.lineTo(px + Math.cos(a) * rr, py + Math.sin(a) * rr);
  }
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = ELEMENT_SWATCH.lava.core;
  ctx.lineWidth = 2;
  ctx.stroke();
  if (Math.random() < dt * 30) particles.embers(wx, m.ry, h, 1);
}

function drawTornado(ctx: Ctx, cam: Camera, m: ViewMarker, time: number, particles: Particles, dt: number): void {
  const u = UNIT;
  const px = cam.sx(m.rx), py = cam.sy(m.ry);
  const fade = Math.min(1, m.t * 2, (MAGICK.tornadoSeconds - m.t) * 3);
  const tint = fedColor(m.el, STEAM.core);
  const fed = m.el.length > 0;
  const rings = 9;
  for (let i = 0; i < rings; i++) {
    const f = i / (rings - 1);
    const y = py - f * TORNADO_H * u;
    const wob = Math.sin(time * 3 + f * 4 + m.id) * (0.15 + f * 0.45) * u;
    const rx = m.r * u * (0.3 + f * 1.15);
    const ry = rx * 0.3;
    const col = fed ? mixHex(tint, '#FFFFFF', 0.25 + 0.35 * f) : mixHex(STEAM.dark, STEAM.core, 0.3 + 0.6 * f);
    ctx.strokeStyle = rgba(col, (0.35 + 0.45 * (1 - f)) * fade);
    ctx.lineWidth = 3 + 3 * f;
    const spin = -time * (9 - f * 3) + i;
    ctx.beginPath(); ctx.ellipse(px + wob, y, rx, ry, 0, spin, spin + 4.4); ctx.stroke();
  }
  if (fed) {
    ctx.globalCompositeOperation = 'lighter';
    drawGlow(ctx, tint, px, py - TORNADO_H * 0.4 * u, 2.6 * u * fade);
    ctx.globalCompositeOperation = 'source-over';
    if (Math.random() < dt * 25) {
      const el = m.el[Math.floor(Math.random() * m.el.length)];
      if (el) particles.burst(el, m.rx, m.ry, Math.random() * TORNADO_H, 1, 3);
    }
  }
  if (Math.random() < dt * 18) {
    const a = Math.random() * TAU;
    particles.spawn(PK.streak, PC.white, m.rx + Math.cos(a) * m.r, m.ry + Math.sin(a) * m.r * 0.5, Math.random() * TORNADO_H, -Math.sin(a) * 8, Math.cos(a) * 4, 0, 0.18, 8, 0, 0.9);
  }
}

/** The elemental is painted at half resolution and scaled up without smoothing, so it sits in the cast's pixel look. */
const GOLEM_PIX = 2;
/** Its canvas, in screen px at UNIT: 4 u wide, 3.4 u tall, feet GOLEM_FEET px from the top. */
const GOLEM_W = 4 * UNIT, GOLEM_H = 3.4 * UNIT, GOLEM_FEET = 3 * UNIT;
/** Weathered stone, browner than the arena's slabs so it stands out on them. */
const GOLEM_LIGHT = '#A2805C', GOLEM_DARK = '#5A4430';
let golemBuf: HTMLCanvasElement | null = null;

function golemCanvas(): CanvasRenderingContext2D | null {
  if (typeof document === 'undefined') return null;
  if (!golemBuf) {
    golemBuf = document.createElement('canvas');
    golemBuf.width = GOLEM_W / GOLEM_PIX;
    golemBuf.height = GOLEM_H / GOLEM_PIX;
  }
  const g = golemBuf.getContext('2d');
  if (!g) return null;
  g.setTransform(1, 0, 0, 1, 0, 0);
  g.clearRect(0, 0, golemBuf.width, golemBuf.height);
  g.setTransform(1 / GOLEM_PIX, 0, 0, 1 / GOLEM_PIX, 0, 0);
  return g;
}

/** A rough boulder: a lit top face over a shaded body, dark outline. */
function stone(g: Ctx, cx: number, cy: number, rx: number, ry: number, seed: number, light: string, dark: string): void {
  g.beginPath();
  for (let i = 0; i < 7; i++) {
    const a = i * (TAU / 7) + hashRand(seed, 1) * 0.5;
    const k = 0.84 + 0.24 * hashRand(seed, 30 + i);
    const x = cx + Math.cos(a) * rx * k, y = cy + Math.sin(a) * ry * k;
    if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
  }
  g.closePath();
  g.fillStyle = dark;
  g.fill();
  g.save();
  g.clip();
  g.fillStyle = light;
  g.beginPath(); g.ellipse(cx - rx * 0.25, cy - ry * 0.35, rx * 0.95, ry * 0.75, 0, 0, TAU); g.fill();
  g.restore();
  g.strokeStyle = '#1E1309';
  g.lineWidth = 3;
  g.stroke();
}

/**
 * The earth elemental: stubby stone legs, a great boulder chest with a
 * glowing core (the colour of what it was fed), shoulder stones, hanging
 * fists and a small head with glowing eyes. It bobs as it walks and brings
 * both fists down when it slams. Taller than a wizard: it fights beside you.
 */
function drawElemental(ctx: Ctx, cam: Camera, m: ViewMarker, time: number, particles: Particles, dt: number): void {
  const u = UNIT;
  const px = cam.sx(m.rx), py = cam.sy(m.ry);
  const rise = Math.min(1, (MAGICK.elementalSeconds - m.t) / 0.5);
  const fade = Math.min(1, m.t * 2);
  const slam = m.act !== undefined && m.act < SLAM_POSE_S ? 1 - m.act / SLAM_POSE_S : 0;
  const core = fedColor(m.el, '#FFB347');
  const face = Math.cos(m.ang) >= 0 ? 1 : -1;
  ctx.save();
  ctx.globalAlpha = fade;
  ctx.globalCompositeOperation = 'lighter';
  drawGlow(ctx, core, px, py - 1.1 * u, (1.6 + 0.8 * slam) * u);
  ctx.globalCompositeOperation = 'source-over';
  const g = golemCanvas();
  if (g && golemBuf) {
    // Local frame: x from the centre, y up from the feet (screen px at UNIT); risen from the ground on summoning.
    const cx = GOLEM_W / 2, fy = GOLEM_FEET + (1 - rise) * ELEMENTAL_H * u;
    const bob = Math.sin(time * 5 + m.id) * 0.05 * u;
    const L = GOLEM_LIGHT, D = GOLEM_DARK;
    const step = Math.sin(time * 6 + m.id) * 0.08 * u;
    stone(g, cx - 0.26 * u, fy - 0.24 * u + step, 0.22 * u, 0.26 * u, m.id + 21, L, D);
    stone(g, cx + 0.26 * u, fy - 0.24 * u - step, 0.22 * u, 0.26 * u, m.id + 22, L, D);
    const chestY = fy - 1.15 * u + bob + slam * 0.18 * u;
    // Fists swing down to the ground in a slam; the far one is drawn first.
    const fistY = fy - (0.7 - 0.55 * slam) * u + bob;
    stone(g, cx - face * 0.72 * u, fistY, 0.25 * u, 0.25 * u, m.id + 11, L, D);
    stone(g, cx, chestY, 0.5 * u, 0.6 * u, m.id + 3, L, D);
    // The core shows through a crack in the chest.
    g.fillStyle = core;
    g.beginPath(); g.ellipse(cx + face * 0.05 * u, chestY + 0.02 * u, 0.15 * u, 0.13 * u, 0, 0, TAU); g.fill();
    g.strokeStyle = core;
    g.lineWidth = 3;
    g.beginPath();
    g.moveTo(cx + face * 0.05 * u, chestY - 0.1 * u); g.lineTo(cx - face * 0.15 * u, chestY - 0.32 * u);
    g.moveTo(cx + face * 0.12 * u, chestY + 0.1 * u); g.lineTo(cx + face * 0.3 * u, chestY + 0.3 * u);
    g.stroke();
    stone(g, cx - face * 0.5 * u, chestY - 0.45 * u, 0.24 * u, 0.2 * u, m.id + 31, L, D);
    stone(g, cx + face * 0.5 * u, chestY - 0.45 * u, 0.24 * u, 0.2 * u, m.id + 32, L, D);
    const headY = chestY - 0.8 * u;
    stone(g, cx + face * 0.06 * u, headY, 0.22 * u, 0.22 * u, m.id + 7, L, D);
    g.fillStyle = core;
    g.fillRect(cx + face * 0.02 * u - 2, headY - 0.03 * u, 4, 4);
    g.fillRect(cx + face * 0.16 * u - 2, headY - 0.03 * u, 4, 4);
    stone(g, cx + face * 0.72 * u, fistY + 0.04 * u, 0.28 * u, 0.27 * u, m.id + 13, L, D);
    const smooth = ctx.imageSmoothingEnabled;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(golemBuf, px - GOLEM_W / 2, py - GOLEM_FEET, GOLEM_W, GOLEM_H);
    ctx.imageSmoothingEnabled = smooth;
  }
  ctx.restore();
  if (slam > 0.9) particles.dust(m.rx, m.ry, 2, 3);
  if (m.el.length > 0 && Math.random() < dt * 10) {
    const el = m.el[Math.floor(Math.random() * m.el.length)];
    if (el) particles.burst(el, m.rx, m.ry, 0.6, 1, 1.5);
  }
}

// ------------------------------------------------------------------ sky

/** The storm cloud above its circle, lit from inside now and then. */
export function drawMarkerSky(ctx: Ctx, cam: Camera, m: ViewMarker, time: number): void {
  if (m.k !== 'storm') return;
  const u = UNIT;
  const fade = Math.min(1, m.t, (MAGICK.stormSeconds - m.t) * 2);
  const px = cam.sx(m.rx), py = cam.sy(m.ry) - STORM_CLOUD_H * u;
  const flash = hashRand(m.id, Math.floor(time * 7)) > 0.82 ? 1 : 0;
  for (let i = 0; i < 9; i++) {
    const a = i * 0.7 + m.id;
    const d = m.r * u * 0.62 * hashRand(m.id, 300 + i);
    const x = px + Math.cos(a) * d + Math.sin(time * 0.4 + i) * 6;
    const y = py + Math.sin(a) * d * 0.35;
    const r = (1.1 + 0.9 * hashRand(m.id, 900 + i)) * u;
    ctx.fillStyle = rgba(flash ? '#8E86B8' : '#3A3D52', 0.55 * fade);
    ctx.beginPath(); ctx.arc(x, y, r, 0, TAU); ctx.fill();
  }
  if (flash) {
    ctx.globalCompositeOperation = 'lighter';
    drawGlow(ctx, ELEMENT_SWATCH.lightning.core, px, py, m.r * u * 1.2 * fade);
    ctx.globalCompositeOperation = 'source-over';
  }
}
